import { useCallback, type Dispatch, type SetStateAction } from "react";
import type { ComposePlan } from "@/lib/manufacturing/composePlan";
import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import {
  applyComposeMatch,
  applyComposeMatchMany,
  applyComposeReclassify,
  applyComposeSplitByStyle,
  applyComposeSplitByStyleMany,
} from "./composePlanWorkflow";

/** Match-desk mutations for a compose plan (single + bulk). */
export function useComposeMatchActions({
  recipes,
  setPlan,
  setError,
  setCommitLog,
}: {
  recipes: readonly MfgRecipeSummary[];
  setPlan: Dispatch<SetStateAction<ComposePlan | null>>;
  setError: (msg: string | null) => void;
  setCommitLog: (msg: string) => void;
}) {
  const matchNode = useCallback(
    (nodeId: string, recipeId: string | null, theoretical: boolean) => {
      setPlan((p) => (p ? applyComposeMatch(p, nodeId, recipeId, theoretical, recipes) : p));
    },
    [recipes, setPlan],
  );

  const matchNodes = useCallback(
    (nodeIds: string[], recipeId: string | null, theoretical: boolean) => {
      if (nodeIds.length === 0) return;
      setPlan((p) =>
        p ? applyComposeMatchMany(p, nodeIds, recipeId, theoretical, recipes) : p,
      );
    },
    [recipes, setPlan],
  );

  const reclassify = useCallback(
    (nodeId: string, kind: MfgRecipeKind) => {
      setPlan((p) => (p ? applyComposeReclassify(p, nodeId, kind) : p));
    },
    [setPlan],
  );

  const splitByStyle = useCallback(
    (nodeId: string) => {
      setPlan((p) => {
        if (!p) return p;
        const res = applyComposeSplitByStyle(p, nodeId);
        if (!res.ok) {
          queueMicrotask(() => setError(res.error));
          return p;
        }
        queueMicrotask(() => {
          setError(null);
          setCommitLog(res.message);
        });
        return res.plan;
      });
    },
    [setCommitLog, setError, setPlan],
  );

  const splitByStyleMany = useCallback(
    (nodeIds: string[]) => {
      if (nodeIds.length === 0) return;
      setPlan((p) => {
        if (!p) return p;
        const res = applyComposeSplitByStyleMany(p, nodeIds);
        if (!res.ok) {
          queueMicrotask(() => setError(res.error));
          return p;
        }
        queueMicrotask(() => {
          setError(null);
          setCommitLog(res.message);
        });
        return res.plan;
      });
    },
    [setCommitLog, setError, setPlan],
  );

  const removeProduct = useCallback(
    (nodeId: string) => {
      setPlan((p) => {
        if (!p) return p;
        const node = p.nodes.find((n) => n.id === nodeId);
        if (!node || node.role !== "product") return p;
        return {
          ...p,
          nodes: p.nodes.filter((n) => n.id !== nodeId),
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [setPlan],
  );

  return {
    matchNode,
    matchNodes,
    reclassify,
    splitByStyle,
    splitByStyleMany,
    removeProduct,
  };
}
